import {
	EventsMultiOptionQueryParam,
	EventsSearchQueryParamKey,
	EventsSearchQueryParams,
	EventsSingleOptionQueryParam,
} from '../models/open-days.models';

/**
 * @param queryParams stored in store
 * @param queryParamKey key of query param to be updated, e.g. loc / type / level
 * @param queryParamValue the new value we want to assign or remove
 * @param addQueryParam true if we are to add the new url_name, false if we are to remove it
 * @returns updated queryParams with new value updated / removed
 */
export const updateQueryParams = (
	queryParams: EventsSearchQueryParams,
	queryParamKey: EventsSearchQueryParamKey,
	queryParamValue: string | null,
	addQueryParam?: boolean,
) => {
	if (!queryParams || !queryParamKey) return queryParams;

	switch (queryParamKey) {
		case 's':
		case 'page':
			return updateQueryParamForSingleSelect(
				queryParams,
				queryParamKey,
				queryParamValue,
			);
		case 'date':
			return updateQueryParamsForCalendar(
				queryParams,
				queryParamKey,
				queryParamValue,
				addQueryParam,
			);
		default:
			return updateQueryParamForMultiSelectCheckbox(
				queryParams,
				queryParamKey,
				queryParamValue,
				addQueryParam,
			);
	}
};

const updateQueryParamForSingleSelect = (
	queryParams: EventsSearchQueryParams,
	queryParamKey: EventsSingleOptionQueryParam,
	queryParamValue: string | null,
) => {
	if (!queryParamValue) {
		delete queryParams[queryParamKey];
		return queryParams;
	}

	return { ...queryParams, [queryParamKey]: queryParamValue };
};

const updateQueryParamForMultiSelectCheckbox = (
	queryParams: EventsSearchQueryParams,
	queryParamKey: EventsMultiOptionQueryParam,
	queryParamValue: string | null,
	addQueryParam: boolean | undefined,
) => {
	if (typeof addQueryParam !== 'boolean' || !queryParamValue) return queryParams;

	const currentValue = queryParams[queryParamKey];

	if (!currentValue) {
		return addQueryParam
			? { ...queryParams, [queryParamKey]: queryParamValue }
			: queryParams;
	}

	if (addQueryParam) {
		return {
			...queryParams,
			[queryParamKey]: `${currentValue}_${queryParamValue}`,
		};
	}

	return deleteQueryParamValue(
		currentValue,
		queryParams,
		queryParamKey,
		queryParamValue,
	);
};

const updateQueryParamsForCalendar = (
	queryParams: EventsSearchQueryParams,
	queryParamKey: 'date',
	queryParamValue: string | null,
	addQueryParam: boolean | undefined,
) => {
	if (!queryParamValue) {
		delete queryParams[queryParamKey];
		return queryParams;
	}

	const currentValue = queryParams[queryParamKey];
	if (!currentValue) {
		return addQueryParam
			? { ...queryParams, [queryParamKey]: queryParamValue }
			: queryParams;
	}

	if (addQueryParam) {
		return { ...queryParams, [queryParamKey]: queryParamValue };
	}

	return queryParams;
};

const deleteQueryParamValue = (
	currentValue: string,
	queryParams: EventsSearchQueryParams,
	queryParamKey: EventsSearchQueryParamKey,
	queryParamValue: string,
) => {
	const queryParamParts = currentValue.split('_');

	if (queryParamParts.length > 1) {
		const newValue = queryParamParts
			.filter((part) => part !== queryParamValue)
			.join('_');
		return { ...queryParams, [queryParamKey]: newValue };
	}

	delete queryParams[queryParamKey];
	return queryParams;
};
